import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, FocusEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DotLoader } from '~components/DotLoader';
import ControlledCheckbox from '~components/Form/ControlledCheckbox';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import OberonDialog from '~components/OberonDialog';
import useDebounce from '~hooks/useDebounce';
import useQueues from '~hooks/useQueues';
import { getRoutingProfiles } from '~pages/CampaignManagement/api';
import {
  CampaignType,
  CreateDiallerGroup,
  DiallerType,
  PushPreviewSeconds,
  RingOutSeconds,
  RoutingProfile,
} from '~pages/CampaignManagement/domain';
import useAccessFilterSearch from '~pages/SystemManagement/AccessFilterList/useAccessFilterSearch';
import { useAppConfiguration } from '~providers/AppConfigurationProvider';
import { useUserPreferences } from '~providers/UserPreferencesProvider';

interface Props {
  open: boolean;
  submitting: boolean;
  onAccept: (data: CreateDiallerGroup) => void;
  onClose: () => void;
}

interface ListItem {
  id: number;
  name: string;
}

interface Form {
  name: string;
  description: string;
  routingProfiles: { label: string; value: string }[];
  diallerType: DiallerType | null;
  campaignType: CampaignType | null;
  enableManualOutboundWhenAvailable: boolean;
  manualOutboundRequireDetails: boolean;
  enablePreparedOutboundHangup: boolean;
  enableManualOutboundWhenOffline: boolean;
  systemDisposedInboundCalls: boolean;
  enableVoicemailMessage: false;
  voicemailMessageARN: string | null;
  // Preview Settings - use if campaignType is preview
  enableEndpointSelection: boolean;
  allowPushPreviewSeconds: boolean;
  pushPreviewSeconds: number;
  allowRingOutSeconds: boolean;
  ringOutSeconds: number;
  // Predictive Settings - use if campaignType is predictive
  ringTimeSeconds: number;
  holdDropSeconds: number;
  maxLinesPerAgent: number;
  lookaheadTimeSeconds: number;
  numReservedAgents: number;
  outboundQueue: string;
  // Extra
  accessFilters: ListItem[];
}

const diallerTypeList = [
  {
    label: 'Connect',
    value: DiallerType.Connect,
  },
  {
    label: 'SIP',
    value: DiallerType.SIP,
  },
];

const asteriskCampaignTypeList = [
  {
    label: 'Predictive',
    value: CampaignType.Predictive,
  },
];

const connectCampaignTypeList = [
  {
    label: 'Preview',
    value: CampaignType.Preview,
  },
  {
    label: 'Predictive',
    value: CampaignType.Predictive,
  },
];

const fetchRoutingProfilesOrEmptyArray = async () => {
  try {
    return await getRoutingProfiles();
  } catch (e) {
    return [];
  }
};

const CreateDiallerGroupModal = ({ open, submitting, onAccept, onClose }: Props) => {
  const appConfig = useAppConfiguration();
  const { accessFilter } = useUserPreferences();
  const [searchAccessFilter, setSearchAccessFilter] = useState<string>('');
  const [fetchingFormData, setFetchingFormData] = useState<boolean>(false);
  const [routingProfiles, setRoutingProfiles] = useState<RoutingProfile[]>([]);
  const debouncedSearchAccessFilter = useDebounce(searchAccessFilter, 500);
  const {
    loading: accessFilterFetching,
    error: accessFilterFetchError,
    list: accessFilters,
    intersectionObserverRef: lastAccessFilterDataElement,
  } = useAccessFilterSearch(debouncedSearchAccessFilter, { archived: false, shouldFetch: open });
  const { loading: loadingQueues, queues } = useQueues();
  const isLoading = fetchingFormData || loadingQueues || accessFilterFetching || submitting;
  const {
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
  } = useForm<Form>({
    defaultValues: {
      name: '',
      description: '',
      routingProfiles: [],
      diallerType: null,
      campaignType: null,
      enableManualOutboundWhenAvailable: false,
      manualOutboundRequireDetails: false,
      enablePreparedOutboundHangup: false,
      enableManualOutboundWhenOffline: false,
      systemDisposedInboundCalls: false,
      enableVoicemailMessage: false,
      voicemailMessageARN: null,
      enableEndpointSelection: false,
      allowPushPreviewSeconds: false,
      pushPreviewSeconds: PushPreviewSeconds.Min,
      allowRingOutSeconds: false,
      ringOutSeconds: RingOutSeconds.Min,
      ringTimeSeconds: 30,
      holdDropSeconds: 5,
      maxLinesPerAgent: 1.4,
      lookaheadTimeSeconds: 30,
      numReservedAgents: 0,
      outboundQueue: '',
      accessFilters: [],
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  const accessFiltersWatch = watch('accessFilters');
  const diallerTypeWatch = watch('diallerType');
  const campaignTypeWatch = watch('campaignType');
  const enableManualOutboundWhenAvailableWatch = watch('enableManualOutboundWhenAvailable');
  const enableManualOutboundWhenOfflineWatch = watch('enableManualOutboundWhenOffline');
  const showManualOutboundOptions = enableManualOutboundWhenAvailableWatch || enableManualOutboundWhenOfflineWatch;
  const enableVoicemailMessageWatch = watch('enableVoicemailMessage');
  const watchAllowPushPreviewSeconds = watch('allowPushPreviewSeconds');
  const watchAllowRingOutSeconds = watch('allowRingOutSeconds');
  const isConnectPredictive = diallerTypeWatch === DiallerType.Connect && campaignTypeWatch === CampaignType.Predictive;
  const campaignTypeList = useMemo<{ label: string; value: string }[]>(() => {
    switch (diallerTypeWatch) {
      case DiallerType.SIP: {
        return asteriskCampaignTypeList;
      }
      case DiallerType.Connect: {
        return connectCampaignTypeList;
      }
      default: {
        return [];
      }
    }
  }, [diallerTypeWatch]);

  useEffect(() => {
    const fetchRoutingProfiles = async () => {
      setFetchingFormData(true);

      const data = await fetchRoutingProfilesOrEmptyArray();
      setRoutingProfiles(data);
      setFetchingFormData(false);
    };

    if (open) {
      if (!appConfig.aws.externallyManagedRoutingProfile) {
        fetchRoutingProfiles();
      }
      if (accessFilter) {
        setValue('accessFilters', [{ id: accessFilter.id, name: accessFilter.name }]);
      }
    }

    return () => {
      reset();
    };
  }, [open]);

  // Clears campaign type if dialler type changes
  useEffect(() => {
    setValue('campaignType', null);
  }, [diallerTypeWatch]);

  // Update push preview seconds with the min default if allowed else of not allowed force to Infinite (0)
  // which indicates infinite preview time
  useEffect(() => {
    if (watchAllowPushPreviewSeconds === true) {
      setValue('pushPreviewSeconds', PushPreviewSeconds.Min);
    } else {
      setValue('pushPreviewSeconds', PushPreviewSeconds.Infinite);
    }
  }, [watchAllowPushPreviewSeconds]);

  // Update ring out seconds with the min default if allowed else of not allowed force to Infinite (0)
  // which indicates that we do not auto ring out and leave it up to the contact service provider
  useEffect(() => {
    if (watchAllowRingOutSeconds === true) {
      setValue('ringOutSeconds', RingOutSeconds.Min);
    } else {
      setValue('ringOutSeconds', RingOutSeconds.Infinite);
    }
  }, [watchAllowRingOutSeconds]);

  const onSubmit = handleSubmit(async (data: Form) => {
    let submitData: CreateDiallerGroup = {
      name: data.name,
      description: data.description || '',
      routingProfiles: (data.routingProfiles || []).map((item: { label: string; value: string }) => item.value),
      diallerType: data.diallerType as DiallerType,
      campaignType: data.campaignType as CampaignType,
      enableManualOutbound: data.enableManualOutboundWhenAvailable,
      manualOutboundRequireDetails: data.manualOutboundRequireDetails,
      enablePreparedOutboundHangup: data.enablePreparedOutboundHangup,
      enableManualOutboundWhenOffline: data.enableManualOutboundWhenOffline,
      systemDisposedInboundCalls: data.systemDisposedInboundCalls,
      previewSettings: undefined,
      predictiveSettings: undefined,
      accessFilterIds: data.accessFilters.length > 0 ? data.accessFilters.map((item) => item.id) : undefined,
      voicemailMessageARN: data.enableVoicemailMessage ? data.voicemailMessageARN || undefined : undefined,
    };

    if (data.campaignType === CampaignType.Preview) {
      submitData = {
        ...submitData,
        previewSettings: {
          enableEndpointSelection: data.enableEndpointSelection,
          pushPreviewSeconds: data.pushPreviewSeconds,
          ringOutSeconds: data.ringOutSeconds,
        },
      };
    } else {
      submitData = {
        ...submitData,
        predictiveSettings: {
          ringTimeSeconds: data.ringTimeSeconds,
          holdDropSeconds: data.holdDropSeconds,
          maxLinesPerAgent: data.maxLinesPerAgent,
          lookaheadTimeSeconds: data.lookaheadTimeSeconds,
          numReservedAgents: data.numReservedAgents,
          outboundQueue: data.outboundQueue || undefined,
        },
      };
    }

    try {
      await onAccept(submitData);
    } catch (e) {
      // Do nothing, catch error to prevent form reset on failed action
      console.error(e);
      return;
    }

    reset();
  });

  const diallerTypeListDisplay = diallerTypeList.map((item, index) => (
    <MenuItem key={index} value={item.value}>
      {item.label}
    </MenuItem>
  ));

  const campaignTypeListDisplay = campaignTypeList.map((item, index) => (
    <MenuItem key={index} value={item.value}>
      {item.label}
    </MenuItem>
  ));

  const routingProfilesList = routingProfiles.map((item: RoutingProfile) => ({
    label: item.name,
    value: item.id,
  }));

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchAccessFilter(e.target.value);
  };

  const onSearchBlur = (e: FocusEvent<HTMLInputElement>) => {
    setSearchAccessFilter('');
  };

  const accessFiltersNoOptionsText = useMemo(() => {
    if (accessFilterFetching) {
      return <DotLoader align='center' />;
    }

    if (accessFilterFetchError) {
      return (
        <Typography variant='body2' align='center' color='textSecondary'>
          Failed to load access filters
        </Typography>
      );
    }

    return undefined;
  }, [accessFilterFetching, accessFilterFetchError]);

  const accessFiltersFilteredList: ListItem[] = useMemo(() => {
    let list: ListItem[] = accessFilters.map((item) => ({ id: item.id, name: item.name }));
    if (accessFilter) {
      list = [...list, { id: accessFilter.id, name: accessFilter.name }];
    }
    if (!accessFiltersWatch) {
      return list;
    }

    return list.filter((listItem) => {
      return !accessFiltersWatch.find((watchItem) => listItem.id === watchItem.id);
    });
  }, [accessFilters, accessFiltersWatch]);

  const queueIdList = Object.keys(queues).sort((a, b) => {
    let an = queues[a]?.name;
    let bn = queues[b]?.name;
    return an == bn ? 0 : an > bn ? 1 : -1;
  });

  return (
    <OberonDialog
      open={open}
      onSubmit={onSubmit}
      onClose={onClose}
      title='Create Dialler Group'
      content={
        <Grid container spacing={2}>
          <Grid size={12}>
            <Controller
              name='name'
              control={control}
              rules={{
                required: 'Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Name'
                  disabled={isLoading}
                  required={true}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='description'
              control={control}
              rules={{}}
              render={({ field }) => (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  label='Description'
                  disabled={isLoading}
                  required={true}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          {!appConfig.aws.externallyManagedRoutingProfile && (
            <Grid size={12}>
              <Controller
                name='routingProfiles'
                control={control}
                rules={{ required: 'At least one routing profile must be selected.' }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    value={field.value}
                    onChange={(e, data) => field.onChange(data)}
                    fullWidth
                    multiple
                    options={routingProfilesList}
                    filterSelectedOptions
                    disabled={isLoading}
                    renderInput={(params) => (
                      <TextField
                        label='Routing Profiles'
                        required={true}
                        error={Boolean(errors.routingProfiles)}
                        helperText={errors.routingProfiles?.message}
                        variant='outlined'
                        {...params}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid size={12}>
            <Controller
              name='diallerType'
              control={control}
              rules={{
                required: 'Dialler type is required.',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  variant='outlined'
                  label='Dialler type'
                  disabled={isLoading}
                  required={true}
                  error={Boolean(errors.diallerType)}
                  helperText={errors.diallerType?.message}
                  value={field.value || ''}>
                  {diallerTypeListDisplay}
                </TextField>
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='campaignType'
              control={control}
              rules={{
                required: 'Campaign type is required.',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  variant='outlined'
                  label='Campaign type'
                  disabled={isLoading || campaignTypeListDisplay.length === 0}
                  required={true}
                  error={Boolean(errors.campaignType)}
                  helperText={errors.campaignType?.message}
                  value={field.value || ''}>
                  {campaignTypeListDisplay}
                </TextField>
              )}
            />
          </Grid>

          {diallerTypeWatch && diallerTypeWatch !== DiallerType.SIP && campaignTypeWatch && (
            <>
              {campaignTypeWatch && campaignTypeWatch !== CampaignType.Predictive && (
                <Grid size={12}>
                  <ControlledCheckbox
                    name='enableManualOutboundWhenAvailable'
                    label='Enable manual outbound when available?'
                    control={control}
                    disabled={submitting}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <ControlledCheckbox
                  name='enableManualOutboundWhenOffline'
                  label='Enable manual outbound when offline?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              {showManualOutboundOptions && (
                <>
                  <Grid size={12}>
                    <ControlledCheckbox
                      name='manualOutboundRequireDetails'
                      label='Manual outbound require details?'
                      control={control}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid size={12}>
                    <ControlledCheckbox
                      name='enablePreparedOutboundHangup'
                      label='Enable prepared outbound hangup?'
                      control={control}
                      disabled={submitting}
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <ControlledCheckbox
                  name='systemDisposedInboundCalls'
                  label='System dispose inbound calls?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              <Grid size={12}>
                <ControlledCheckbox
                  name='enableVoicemailMessage'
                  label='Enable voicemail message?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              {enableVoicemailMessageWatch && (
                <>
                  <Grid size={12}>
                    <Controller
                      name='voicemailMessageARN'
                      control={control}
                      rules={{
                        required: 'Voicemail message ARN is required.',
                        validate: (value) => {
                          if (!value || (value && value.length === 0)) return undefined;

                          if (!value.startsWith('arn:')) {
                            return 'Voicemail message ARN must be a valid ARN.';
                          }

                          return undefined;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant='outlined'
                          label='Voicemail message ARN'
                          disabled={submitting}
                          required={true}
                          error={Boolean(errors.voicemailMessageARN)}
                          helperText={errors.voicemailMessageARN?.message}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {campaignTypeWatch === CampaignType.Preview && (
            <>
              <Grid size={12}>
                <ControlledCheckbox
                  name='enableEndpointSelection'
                  label='Enable endpoint selection?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              <Grid size={12}>
                <ControlledCheckbox
                  name='allowPushPreviewSeconds'
                  label='Allow push preview seconds?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              {watchAllowPushPreviewSeconds && (
                <Grid size={12}>
                  <ControlledNumberField
                    name='pushPreviewSeconds'
                    control={control}
                    rules={{
                      required: 'push preview seconds is required.',
                      min: {
                        value: PushPreviewSeconds.Min,
                        message: `push preview seconds cannot be lower than ${PushPreviewSeconds.Min}.`,
                      },
                      max: {
                        value: PushPreviewSeconds.Max,
                        message: `push preview seconds cannot be greater than ${PushPreviewSeconds.Max}.`,
                      },
                    }}
                    label='Push preview seconds'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.pushPreviewSeconds)}
                    helperText={errors.pushPreviewSeconds?.message}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <ControlledCheckbox
                  name='allowRingOutSeconds'
                  label='Allow ring out seconds?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              {watchAllowRingOutSeconds && (
                <Grid size={12}>
                  <ControlledNumberField
                    name='ringOutSeconds'
                    control={control}
                    rules={{
                      required: 'ring out seconds is required.',
                      min: {
                        value: RingOutSeconds.Min,
                        message: `ring out seconds cannot be lower than ${RingOutSeconds.Min}.`,
                      },
                      max: {
                        value: RingOutSeconds.Max,
                        message: `ring out seconds cannot be greater than ${RingOutSeconds.Max}.`,
                      },
                    }}
                    label='Ring out seconds'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.ringOutSeconds)}
                    helperText={errors.ringOutSeconds?.message}
                  />
                </Grid>
              )}
            </>
          )}

          {campaignTypeWatch === CampaignType.Predictive && (
            <>
              <Grid size={12}>
                <ControlledNumberField
                  name='holdDropSeconds'
                  control={control}
                  rules={{
                    required: 'hold drop seconds is required.',
                  }}
                  label='Hold drop seconds'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.holdDropSeconds)}
                  helperText={errors.holdDropSeconds?.message}
                />
              </Grid>

              <Grid size={12}>
                <ControlledNumberField
                  name='maxLinesPerAgent'
                  control={control}
                  rules={{
                    required: 'max lines per agent is required.',
                  }}
                  label='Max lines per agent'
                  disabled={submitting}
                  required={true}
                  isFloat={true}
                  error={Boolean(errors.maxLinesPerAgent)}
                  helperText={errors.maxLinesPerAgent?.message}
                />
              </Grid>

              <Grid size={12}>
                <ControlledNumberField
                  name='lookaheadTimeSeconds'
                  control={control}
                  rules={{
                    required: 'lookahead time seconds is required.',
                  }}
                  label='Lookahead time seconds'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.lookaheadTimeSeconds)}
                  helperText={errors.lookaheadTimeSeconds?.message}
                />
              </Grid>

              <Grid size={12}>
                <ControlledNumberField
                  name='ringTimeSeconds'
                  control={control}
                  rules={{
                    required: 'ring time seconds is required.',
                  }}
                  label='Ring time seconds'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.ringTimeSeconds)}
                  helperText={errors.ringTimeSeconds?.message}
                />
              </Grid>

              <Grid size={12}>
                <ControlledNumberField
                  name='numReservedAgents'
                  control={control}
                  rules={{
                    required: 'number of reserved agents is required.',
                  }}
                  label='Number of reserved agents'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.numReservedAgents)}
                  helperText={errors.numReservedAgents?.message}
                />
              </Grid>

              {isConnectPredictive && (
                <Grid size={12}>
                  <Controller
                    name='outboundQueue'
                    control={control}
                    rules={{
                      required: 'Outbound queue is required',
                    }}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        fullWidth
                        onChange={(e, data) => {
                          field.onChange(data);
                        }}
                        options={queueIdList}
                        disabled={isLoading}
                        disableClearable
                        getOptionLabel={(option) => queues[option]?.name || ''}
                        renderOption={({ key, ...optionProps }, option) => (
                          <li {...optionProps} key={option}>
                            <Typography variant='body1' color='textPrimary' component='p'>
                              {queues[option]?.name}
                            </Typography>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Outbound Queue'
                            variant='outlined'
                            required={true}
                            error={Boolean(errors.outboundQueue)}
                            helperText={errors.outboundQueue?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid size={12}>
            <Controller
              name='accessFilters'
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  fullWidth
                  multiple
                  onChange={(e, data) => {
                    field.onChange(data);
                  }}
                  options={accessFiltersFilteredList}
                  noOptionsText={accessFiltersNoOptionsText}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isLoading}
                  getOptionLabel={(option) => option.name || ''}
                  renderOption={({ key, ...optionProps }, option) => (
                    <li {...optionProps} ref={lastAccessFilterDataElement} key={option.id}>
                      <Typography variant='body1' color='textPrimary' component='p'>
                        {option.name}
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Access filters'
                      variant='outlined'
                      onBlur={onSearchBlur}
                      onChange={onSearchChange}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
      }
      actionFooter={
        <>
          <Button variant='text' disabled={isLoading} onClick={onClose}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={isLoading}
            loading={isLoading}>
            Create
          </LoadingButton>
        </>
      }
    />
  );
};

export default CreateDiallerGroupModal;
